import { type ComponentProps, isValidElement, type ReactNode } from 'react'

import { FormattedMessage as BaseFormattedMessage, useIntl } from 'react-intl'

import { DefaultError } from '@subaio/utils'

import type { ChildrenProps } from '../DefaultElementProps'
import { FormattedMessage as WrappedFormattedMessage } from '../Formatted'
import { useLoading } from '../Loading'
import { Placeholder } from '../Placeholder'

type FormattedMessageProps = ComponentProps<typeof BaseFormattedMessage>

export const useFormattedText = (content: ReactNode): string => {
	const isLoading = useLoading()
	const { formatMessage: ignoredFormatMessage } = useIntl()

	if (isLoading) {
		return ''
	}

	const mapValue = (value: ReactNode): string => {
		if (value === null || value === undefined) {
			return ''
		}
		if (typeof value === 'string') {
			return value
		}
		if (typeof value === 'number') {
			return String(value)
		}
		if (Array.isArray(value)) {
			return value.map(mapValue).join('')
		}
		if (
			isValidElement<FormattedMessageProps>(value) &&
			(value.type === BaseFormattedMessage || value.type === WrappedFormattedMessage)
		) {
			const { id, defaultMessage, values } = value.props
			// This wrapper does not need to adhere to the defaultMessage rule
			return ignoredFormatMessage({ id, defaultMessage }, values as Record<string, string | number>)
		}
		if (isValidElement<ChildrenProps>(value) && value.type === Placeholder) {
			return mapValue(value.props.children)
		}
		throw new DefaultError('Cannot format title message, must be either a string or a FormattedMessage', { content })
	}

	return mapValue(content)
}
